import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  ArrowBack,
  Download,
  Edit,
  Refresh
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { createApiClient } from '../../utils/api';
import { formatReportMonth, formatFileSize } from '../../utils/formatters';
import { useCurrentUser } from '../../hooks/useApi';
import { Navigate } from 'react-router-dom';
import { useSnackbar } from '../../contexts/NotificationContext';

export const ReportDetailAdmin: React.FC = () => {
  const { marketId, reportId } = useParams<{ marketId: string; reportId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { data: user } = useCurrentUser();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const api = createApiClient(authenticatedFetch);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editFormData, setEditFormData] = React.useState({
    shortDescription: '',
    mediumDescription: '',
    longDescription: ''
  });

  // Redirect if user doesn't have admin permission
  if (!user?.permissions?.includes('market_compass.admin')) {
    return <Navigate to="/" replace />;
  }

  // Fetch report details
  const { data: report, isLoading: loadingReport } = useQuery({
    queryKey: ['admin', 'report', reportId],
    queryFn: async () => {
      const response = await api.get('/api/marketcompass/admin/reports/all');
      return response.data.reports.find((r: any) => r.id === parseInt(reportId!));
    }
  });

  // Fetch market details
  const { data: market } = useQuery({
    queryKey: ['admin', 'market', marketId],
    queryFn: async () => {
      const response = await api.get('/api/marketcompass/admin/markets/all');
      const markets = response.data?.markets || [];
      return markets.find((m: any) => m.id === parseInt(marketId!));
    }
  });

  // Update report mutation
  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      return api.put(`/api/marketcompass/admin/reports/${reportId}/update`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'report', reportId] });
      showSnackbar('Report updated successfully', 'success');
      setEditDialogOpen(false);
    },
    onError: (error: any) => {
      showSnackbar(error.message || 'Failed to update report', 'error');
    }
  });


  if (loadingReport) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading report details...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!report) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ my: 4 }}>
          Report not found.
        </Alert>
      </Container>
    );
  }

  const handleDownload = async () => {
    try {
      // Get download URL from API
      const response = await api.get(`/api/marketcompass/markets/${marketId}/reports/${reportId}/download-url`);
      
      if (response.data?.downloadUrl) {
        // Open download URL in new tab
        window.open(response.data.downloadUrl, '_blank');
        showSnackbar('Download started', 'success');
      } else {
        showSnackbar('Unable to get download URL', 'error');
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.error || 'Failed to download report', 'error');
    }
  };

  const handleEdit = () => {
    if (report) {
      setEditFormData({
        shortDescription: report.short_description || '',
        mediumDescription: report.medium_description || '',
        longDescription: report.long_description || ''
      });
      setEditDialogOpen(true);
    }
  };

  const handleEditSubmit = () => {
    updateMutation.mutate(editFormData);
  };

  const handleRefresh = () => {
    // Invalidate queries for this report to refresh data
    queryClient.invalidateQueries({ queryKey: ['admin', 'report', reportId] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'market', marketId] });
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/admin/market/${marketId}`)}>
            <ArrowBack />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              {market?.name} - Report Details
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {formatReportMonth(report.report_month)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleDownload}
            >
              Download
            </Button>
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={handleEdit}
            >
              Edit
            </Button>
            <IconButton onClick={handleRefresh} size="large">
              <Refresh />
            </IconButton>
          </Box>
        </Box>

        {/* Report Information */}
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Report Information
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Report ID
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {report.id}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Status
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={report.is_published ? 'Published' : 'Draft'}
                        color={report.is_published ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Report Month
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {formatReportMonth(report.report_month)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Market
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {report.market_name} ({report.country_name})
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Descriptions
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Short Description
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {report.short_description}
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Medium Description
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {report.medium_description}
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Long Description
                </Typography>
                <Typography variant="body1">
                  {report.long_description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  File Information
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle2" color="text.secondary">
                  File Name
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, wordBreak: 'break-all' }}>
                  {report.file_name}
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary">
                  File Size
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {formatFileSize(report.file_size_bytes)}
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary">
                  Content Type
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {report.file_content_type}
                </Typography>
                
                {report.azure_blob_name && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary">
                      Azure Blob Name
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, wordBreak: 'break-all' }}>
                      {report.azure_blob_name}
                    </Typography>
                  </>
                )}
                
                {report.azure_container_name && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary">
                      Azure Container
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      {report.azure_container_name}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Timestamps
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle2" color="text.secondary">
                  Created At
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {new Date(report.created_at).toLocaleString()}
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary">
                  Updated At
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {new Date(report.updated_at).toLocaleString()}
                </Typography>
                
                {report.published_at && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary">
                      Published At
                    </Typography>
                    <Typography variant="body1">
                      {new Date(report.published_at).toLocaleString()}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Edit Report Descriptions</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="Short Description"
                value={editFormData.shortDescription}
                onChange={(e) => setEditFormData({ ...editFormData, shortDescription: e.target.value })}
                required
                inputProps={{ maxLength: 255 }}
                helperText={`${editFormData.shortDescription.length}/255 characters`}
              />
              <TextField
                label="Medium Description"
                value={editFormData.mediumDescription}
                onChange={(e) => setEditFormData({ ...editFormData, mediumDescription: e.target.value })}
                required
                multiline
                rows={3}
                inputProps={{ maxLength: 1000 }}
                helperText={`${editFormData.mediumDescription.length}/1000 characters`}
              />
              <TextField
                label="Long Description"
                value={editFormData.longDescription}
                onChange={(e) => setEditFormData({ ...editFormData, longDescription: e.target.value })}
                required
                multiline
                rows={5}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleEditSubmit}
              variant="contained"
              disabled={updateMutation.isPending}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};